if (!customElements.get('product-form')) {
  const FITMENT_SELECTION_MESSAGE = 'Select your Jeep to confirm fitment before adding this product to your cart.';
  const FITMENT_NONFIT_MESSAGE = 'Accept the fitment disclaimer to continue, or choose another Jeep.';

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.fitmentGateEnabled = false;
        this.fitmentGateState = null;
        this.fitmentPanel = null;
        this.fitmentResultState = null;
        this.fitmentVehicleName = null;
        this.fitmentAcknowledgement = null;

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }

        this.setupFitmentGate();
      }
    }

    setupFitmentGate() {
      this.fitmentPanel = this.closest('.js-product')?.querySelector('[data-gv-product-fitment]');
      if (!this.fitmentPanel) return;

      const nonFitmentEl = this.fitmentPanel.querySelector('#gv-mmy-desc-nonfit');
      const fitmentEl = this.fitmentPanel.querySelector('#gv-mmy-desc');
      const universalFitmentEl = this.fitmentPanel.querySelector('#gv-mmy-desc-universal');
      const fallbackEl = this.fitmentPanel.querySelector('#gv-mmy-desc-fallback');
      this.fitmentAcknowledgement = this.fitmentPanel.querySelector('[data-gv-fitment-acknowledgement]');

      this.fitmentAcknowledgement?.addEventListener(
        'change',
        this.handleFitmentAcknowledgementChange.bind(this)
      );

      this.closest('.js-product')?.querySelector('variant-picker')?.addEventListener(
        'on:variant:change',
        this.handleFitmentVariantChange.bind(this)
      );

      this.form.addEventListener('click', this.handleFitmentPurchaseAttempt.bind(this), true);
      if (theme.settings.afterAtc === 'no-js') {
        this.form.addEventListener('submit', this.handleNoJsFitmentSubmit.bind(this));
      }
      this.handleFitmentReady('pending', fallbackEl);

      const checkFitmentState = () => {
        const fitmentVisible = this.isElementVisible(fitmentEl);
        const universalFitmentVisible = this.isElementVisible(universalFitmentEl);
        const nonFitmentVisible = this.isElementVisible(nonFitmentEl);

        if (universalFitmentVisible) {
          this.handleFitmentReady('universal', universalFitmentEl);
          return true;
        }

        if (fitmentVisible) {
          this.handleFitmentReady('fit', fitmentEl);
          return true;
        }

        if (nonFitmentVisible) {
          this.handleFitmentReady('nonfit', nonFitmentEl);
          return true;
        }

        return false;
      };

      const observer = new MutationObserver(() => {
        if (!checkFitmentState()) this.handleFitmentReady('pending', fallbackEl);
      });

      [nonFitmentEl, fitmentEl, universalFitmentEl].filter(Boolean).forEach((element) => {
        observer.observe(element, { attributes: true, attributeFilter: ['style', 'class'] });
      });

      checkFitmentState();

      this.paymentObserver = new MutationObserver(() => {
        this.togglePaymentButtons();
      });

      this.paymentObserver.observe(this.form, { childList: true, subtree: true });
    }

    handleNoJsFitmentSubmit(evt) {
      if (!this.isFitmentSelectionRequired()) return;
      evt.preventDefault();
      this.showFitmentPrompt();
    }

    isElementVisible(element) {
      if (!element) return false;
      return window.getComputedStyle(element).display !== 'none';
    }

    handleFitmentReady(type, element) {
      const vehicleName = type === 'pending' ? null : this.getVehicleName(element);
      const resultChanged = this.fitmentResultState !== type
        || this.fitmentVehicleName !== vehicleName;

      if (resultChanged) this.resetFitmentAcknowledgement();
      this.fitmentResultState = type;
      this.fitmentVehicleName = vehicleName;

      if (vehicleName) {
        this.setPropertyInput('Vehicle', vehicleName);
      } else if (type === 'pending') {
        this.removePropertyInput('Vehicle');
      }

      if (type === 'nonfit') {
        this.setPropertyInput('Fitment', `Fitment not confirmed for vehicle ${vehicleName || 'selected'}`);
        if (this.fitmentAcknowledgement?.checked) {
          this.acceptFitmentAcknowledgement();
        } else {
          this.disablePurchaseButtons('nonfit');
        }
      } else if (type === 'pending') {
        this.removePropertyInput('Fitment');
        this.disablePurchaseButtons('pending');
      } else {
        this.removePropertyInput('Fitment');
        this.enablePurchaseButtons();
      }
    }

    handleFitmentAcknowledgementChange() {
      if (this.fitmentResultState !== 'nonfit' || !this.fitmentAcknowledgement?.checked) {
        this.resetFitmentAcknowledgement();
        if (this.fitmentResultState === 'nonfit') this.disablePurchaseButtons('nonfit');
        return;
      }

      this.acceptFitmentAcknowledgement();
    }

    handleFitmentVariantChange() {
      this.resetFitmentAcknowledgement();
      if (this.fitmentResultState === 'nonfit') this.disablePurchaseButtons('nonfit');
    }

    acceptFitmentAcknowledgement() {
      const vehicleName = this.fitmentVehicleName || 'selected vehicle';
      this.setPropertyInput(
        'Fitment acknowledgment',
        `Accepted — fitment not confirmed for ${vehicleName}`
      );
      this.allowPurchaseButtons('nonfit');
    }

    resetFitmentAcknowledgement() {
      if (this.fitmentAcknowledgement) this.fitmentAcknowledgement.checked = false;
      this.removePropertyInput('Fitment acknowledgment');
      this.fitmentPanel
        ?.querySelector('[data-gv-fitment-acknowledgement-label]')
        ?.classList.remove('is-error');
    }

    getVehicleName(element) {
      if (!element) return null;
      const vehicleLabel = element.querySelector('.title');
      return vehicleLabel ? vehicleLabel.textContent.trim() : null;
    }

    setPropertyInput(propertyName, value) {
      if (!this.form || !propertyName) return;
      const inputName = `properties[${propertyName}]`;
      let input = this.form.querySelector(`input[name="${inputName}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        this.form.appendChild(input);
      }
      input.value = value;
    }

    removePropertyInput(propertyName) {
      if (!this.form || !propertyName) return;
      const inputName = `properties[${propertyName}]`;
      const input = this.form.querySelector(`input[name="${inputName}"]`);
      if (input) input.remove();
    }

    isFitmentSelectionRequired() {
      return this.fitmentGateEnabled;
    }

    handleFitmentPurchaseAttempt(evt) {
      if (!this.isFitmentSelectionRequired()) return;

      const target = evt.target instanceof Element ? evt.target : null;
      const purchaseTrigger = target?.closest(
        '[name="add"], .shopify-payment-button, .shopify-payment-button__button, .shopify-payment-button__more-options, shopify-accelerated-checkout'
      );

      if (!purchaseTrigger || !this.form.contains(purchaseTrigger)) return;

      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      this.showFitmentPrompt();
    }

    showFitmentPrompt() {
      const message = this.fitmentGateState === 'nonfit'
        ? FITMENT_NONFIT_MESSAGE
        : FITMENT_SELECTION_MESSAGE;
      this.setErrorMsgState(message);
      this.fitmentPanel?.classList.add('fitment-warning--error');

      if (this.fitmentGateState === 'nonfit' && this.fitmentAcknowledgement) {
        this.fitmentPanel
          ?.querySelector('[data-gv-fitment-acknowledgement-label]')
          ?.classList.add('is-error');
        this.fitmentAcknowledgement.focus();
        return;
      }

      const selectorButton = this.fitmentPanel?.querySelector('[data-gv-vehicle-selector]');
      if (selectorButton) selectorButton.click();
    }

    clearFitmentPrompt() {
      if ([FITMENT_SELECTION_MESSAGE, FITMENT_NONFIT_MESSAGE].includes(this.errorMsg?.textContent)) {
        this.setErrorMsgState();
      }
      this.fitmentPanel?.classList.remove('fitment-warning--error');
      this.fitmentPanel
        ?.querySelector('[data-gv-fitment-acknowledgement-label]')
        ?.classList.remove('is-error');
    }

    disablePurchaseButtons(state = 'pending') {
      this.fitmentGateEnabled = true;
      this.fitmentGateState = state;
      if (this.submitBtn) {
        this.submitBtn.setAttribute('aria-disabled', 'true');
      }
      this.togglePaymentButtons();
    }

    enablePurchaseButtons() {
      this.allowPurchaseButtons();
    }

    allowPurchaseButtons(state = null) {
      this.fitmentGateEnabled = false;
      this.fitmentGateState = state;
      this.clearFitmentPrompt();
      if (this.submitBtn) {
        this.submitBtn.removeAttribute('aria-disabled');
      }
      this.togglePaymentButtons();
    }

    togglePaymentButtons() {
      const paymentButton = this.form?.querySelector('.shopify-payment-button');
      if (!paymentButton) return;

      paymentButton.style.pointerEvents = this.fitmentGateEnabled ? 'none' : '';
      paymentButton.style.opacity = this.fitmentGateEnabled ? '0.5' : '';
      paymentButton.setAttribute('aria-disabled', this.fitmentGateEnabled ? 'true' : 'false');
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.isFitmentSelectionRequired()) {
        this.showFitmentPrompt();
        return;
      }

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.disabled = false;
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
